import { useState, useEffect, useCallback } from "react";
import OffersModuleLayout from "./OffersModuleLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API_URL, API_BASE_URL } from "../config/api";
import LCLSalesTariffBuilder from "../components/LCLSalesTariffBuilder";
import {
  Upload,
  Train,
  Ship,
  Package,
  Trash2,
  FileSpreadsheet,
  Calendar,
  CheckCircle,
  AlertCircle,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Building2,
  Sparkles,
} from "lucide-react";


// Vendor configurations
const VENDORS = {
  metrans: {
    id: "metrans",
    name: "Metrans",
    type: "rail",
    icon: Train,
    color: "orange",
    description: "FCL Rail transport across EU (HU, PL, CZ, SK, RO)",
    fields: [
      { name: "destination_country", label: "Destination Country", type: "select", options: ["HU", "PL", "CZ", "SK", "RO"] },
      { name: "origin_ports", label: "Origin Ports", type: "text", default: "KOPER,RIJEKA" },
      { name: "direction", label: "Direction", type: "select", options: ["IMPORT", "EXPORT", "BOTH"] },
    ]
  },
  freightlinks: {
    id: "freightlinks",
    name: "Freightlinks",
    type: "lcl",
    icon: Package,
    color: "green",
    description: "LCL Primary vendor - Import & Export",
    fields: [
      { name: "direction", label: "Direction", type: "select", options: ["import", "export", "both"] },
      { name: "export_first_leg_addon", label: "Export 1st Leg Addon (USD/W/M)", type: "number", default: 35, hint: "Koper-Singapore leg" },
    ]
  },
  shipco: {
    id: "shipco",
    name: "Shipco (SSCSI)",
    type: "lcl",
    icon: Ship,
    color: "blue",
    description: "LCL Reference/Competition rates",
    fields: [
      { name: "direction", label: "Direction", type: "select", options: ["import", "export", "both"] },
    ]
  },
  other: {
    id: "other",
    name: "Other Vendor",
    type: "lcl",
    icon: Building2,
    color: "slate",
    description: "Other LCL vendors for reference",
    fields: [
      { name: "vendor_name", label: "Vendor Name", type: "text" },
      { name: "direction", label: "Direction", type: "select", options: ["import", "export", "both"] },
    ]
  }
};

export default function RateCardsPage() {
  const [rateCards, setRateCards] = useState({ metrans: [], lcl: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    name: "",
    validity_start: "",
    validity_end: "",
    file: null,
  });
  const [dragActive, setDragActive] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  // Internal tab: "vendor-cards" (existing upload+list) | "sales-tariff" (new
  // LCL import sell-tariff builder with markup, competitor flagging and
  // Europacific-branded XLSX export).
  const [activeSubTab, setActiveSubTab] = useState("vendor-cards");

  const token = localStorage.getItem("token");

  // Fetch all rate cards
  const fetchRateCards = useCallback(async () => {
    setLoading(true);
    try {
      const [metransRes, lclRes] = await Promise.all([
        axios.get(`${API_URL}/pricing/metrans/rate-cards`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/pricing/lcl/rate-cards`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRateCards({
        metrans: metransRes.data || [],
        lcl: lclRes.data || []
      });
    } catch (error) {
      console.error("Error fetching rate cards:", error);
      toast.error("Failed to load rate cards");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRateCards();
  }, [fetchRateCards]);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setUploadForm(prev => ({ ...prev, file }));
      
      // Auto-generate name from filename
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      setUploadForm(prev => ({ ...prev, name: baseName }));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadForm(prev => ({ ...prev, file }));
      
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      setUploadForm(prev => ({ ...prev, name: baseName }));
    }
  };

  // Upload rate card
  const handleUpload = async () => {
    if (!selectedVendor || !uploadForm.file) {
      toast.error("Please select a vendor and file");
      return;
    }

    if (!uploadForm.validity_start || !uploadForm.validity_end) {
      toast.error("Please set validity dates");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("name", uploadForm.name || uploadForm.file.name);
    formData.append("validity_start", uploadForm.validity_start);
    formData.append("validity_end", uploadForm.validity_end);

    // Add vendor-specific fields
    const vendorConfig = VENDORS[selectedVendor];
    if (vendorConfig.fields) {
      vendorConfig.fields.forEach(field => {
        const value = uploadForm[field.name] || field.default || "";
        formData.append(field.name, value);
      });
    }

    // Determine endpoint and add required fields
    let endpoint;
    if (selectedVendor === "metrans") {
      endpoint = `${API_URL}/pricing/metrans/upload`;
    } else {
      endpoint = `${API_URL}/pricing/lcl/upload`;
      formData.append("vendor", selectedVendor === "other" ? uploadForm.vendor_name : selectedVendor);
      formData.append("vendor_type", selectedVendor === "freightlinks" ? "primary" : "reference");
    }

    try {
      // Transient Cloudflare 520/522/524s (busy/restarting origin) otherwise
      // surface as raw HTML error pages. Retry each network call with backoff.
      const postWithRetry = async (url, body, cfg, attempts = 4) => {
        let lastErr;
        for (let a = 0; a < attempts; a++) {
          try {
            return await axios.post(url, body, cfg);
          } catch (err) {
            const st = err.response?.status;
            const retriable = !err.response || (st >= 500 && st < 600) || st === 429;
            lastErr = err;
            if (!retriable || a === attempts - 1) throw err;
            await new Promise((r) => setTimeout(r, 800 * Math.pow(2, a)));
          }
        }
        throw lastErr;
      };

      // Large files exceed the production ingress body-size limit and can also
      // trip Cloudflare buffering. Upload them in small (256KB) chunks, then
      // pass a file_token; small files go straight through as before.
      const CHUNK_THRESHOLD = 700 * 1024;
      const file = uploadForm.file;
      if (file.size > CHUNK_THRESHOLD) {
        const chunkSize = 256 * 1024;
        const totalChunks = Math.ceil(file.size / chunkSize);
        const uploadId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        for (let i = 0; i < totalChunks; i++) {
          const blob = file.slice(i * chunkSize, (i + 1) * chunkSize);
          const chunkForm = new FormData();
          chunkForm.append("upload_id", uploadId);
          chunkForm.append("chunk_index", i);
          chunkForm.append("total_chunks", totalChunks);
          chunkForm.append("filename", file.name);
          chunkForm.append("chunk", blob, file.name);
          await postWithRetry(`${API_URL}/uploads/chunk`, chunkForm, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        formData.append("file_token", uploadId);
      } else {
        formData.append("file", file);
      }

      const response = await postWithRetry(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      // LCL imports run in the BACKGROUND (large files would otherwise hit the
      // 30s gateway timeout). Poll the job until it finishes.
      if (response.data?.job_id) {
        const jobId = response.data.job_id;
        toast.info("Processing rate card... this can take up to a minute for large files.");
        let done = false;
        for (let attempt = 0; attempt < 90 && !done; attempt++) {
          await new Promise((r) => setTimeout(r, 2000));
          const statusRes = await axios.get(`${API_URL}/pricing/lcl/upload-status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const job = statusRes.data;
          if (job.status === "done") {
            done = true;
            toast.success(`Imported ${job.result?.entries_imported ?? 0} rate entries`);
          } else if (job.status === "error") {
            done = true;
            throw new Error(job.error || "Rate card processing failed");
          }
        }
        if (!done) throw new Error("Still processing — check the rate cards list in a minute.");
      } else {
        toast.success(`Imported ${response.data.entries_imported} rate entries`);
      }
      fetchRateCards();
      
      // Reset form
      setSelectedVendor(null);
      setUploadForm({
        name: "",
        validity_start: "",
        validity_end: "",
        file: null,
      });
    } catch (error) {
      console.error("Upload error:", error);
      // Handle various error response formats
      let errorMessage = "Failed to upload rate card";
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors are arrays of objects
        errorMessage = detail.map(err => 
          typeof err === "object" ? (err.msg || err.message || JSON.stringify(err)) : err
        ).join(", ");
      } else if (detail && typeof detail === "object") {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      } else if (status >= 500 && status < 600) {
        // Cloudflare/origin returned an error page (often raw HTML) — give a
        // clean, actionable message instead of dumping the 520 page.
        errorMessage = "The server was briefly unavailable while uploading. Please try again in a moment — your progress was not saved.";
      } else if (error.message && !error.response) {
        // Thrown from the background-job polling (job error / timeout) or a
        // network drop after retries were exhausted.
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Delete rate card
  const handleDelete = async (type, cardId) => {
    if (!confirm("Are you sure you want to delete this rate card?")) return;
    
    try {
      const endpoint = type === "metrans" 
        ? `${API_URL}/pricing/metrans/rate-cards/${cardId}`
        : `${API_URL}/pricing/lcl/rate-cards/${cardId}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Rate card deleted");
      fetchRateCards();
    } catch (error) {
      toast.error("Failed to delete rate card");
    }
  };

  const toggleExpand = (cardId) => {
    setExpandedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  return (
    <OffersModuleLayout>
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Rate Cards</h1>
            <p className="text-slate-600">Manage FCL rail and LCL ocean freight rate cards</p>
          </div>
          <Button onClick={fetchRateCards} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Sub-tab switcher */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-6 w-fit">
          <button
            onClick={() => setActiveSubTab("vendor-cards")}
            data-testid="rate-cards-subtab-vendor"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeSubTab === "vendor-cards"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            Vendor Rate Cards
          </button>
          <button
            onClick={() => setActiveSubTab("sales-tariff")}
            data-testid="rate-cards-subtab-tariff"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeSubTab === "sales-tariff"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Sparkles className="h-4 w-4 inline mr-2" />
            LCL Import Sales Tariff
          </button>
          <button
            onClick={() => setActiveSubTab("export-tariff")}
            data-testid="rate-cards-subtab-export-tariff"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeSubTab === "export-tariff"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Sparkles className="h-4 w-4 inline mr-2" />
            LCL Export Sales Tariff
          </button>
        </div>

        {activeSubTab === "sales-tariff" ? (
          <LCLSalesTariffBuilder mode="import" />
        ) : activeSubTab === "export-tariff" ? (
          <LCLSalesTariffBuilder mode="export" />
        ) : (
        <>
        {/* Upload Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Upload Rate Card</h2>
          
          {/* Vendor Selection */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="vendor-selection">
            {Object.entries(VENDORS).map(([key, vendor]) => (
              <button
                key={key}
                data-testid={`vendor-btn-${key}`}
                onClick={() => setSelectedVendor(key)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedVendor === key
                    ? `border-${vendor.color}-500 bg-${vendor.color}-50 ring-2 ring-${vendor.color}-500`
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                style={{
                  borderColor: selectedVendor === key ? (vendor.color === 'orange' ? '#f97316' : vendor.color === 'green' ? '#22c55e' : vendor.color === 'blue' ? '#3b82f6' : '#64748b') : undefined,
                  backgroundColor: selectedVendor === key ? (vendor.color === 'orange' ? '#fff7ed' : vendor.color === 'green' ? '#f0fdf4' : vendor.color === 'blue' ? '#eff6ff' : '#f8fafc') : undefined,
                }}
              >
                <vendor.icon className={`h-6 w-6 mb-2 ${
                  vendor.color === 'orange' ? 'text-orange-600' :
                  vendor.color === 'green' ? 'text-green-600' :
                  vendor.color === 'blue' ? 'text-blue-600' : 'text-slate-600'
                }`} />
                <p className="font-medium text-slate-900">{vendor.name}</p>
                <p className="text-xs text-slate-500 mt-1">{vendor.description}</p>
              </button>
            ))}
          </div>

          {/* Upload Form */}
          <AnimatePresence>
            {selectedVendor && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                {/* Drag & Drop Zone */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
                  }`}
                >
                  {uploadForm.file ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileSpreadsheet className="h-8 w-8 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{uploadForm.file.name}</p>
                        <p className="text-sm text-slate-500">
                          {(uploadForm.file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setUploadForm(prev => ({ ...prev, file: null }))}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 mb-2">
                        Drag & drop your Excel/CSV file here, or
                      </p>
                      <label className="cursor-pointer">
                        <span className="text-blue-600 hover:underline">browse files</span>
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv,.msg"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                    </>
                  )}
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Rate Card Name</label>
                    <Input
                      value={uploadForm.name}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Metrans Hungary 2026"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Valid From</label>
                    <Input
                      type="date"
                      value={uploadForm.validity_start}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, validity_start: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Valid Until</label>
                    <Input
                      type="date"
                      value={uploadForm.validity_end}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, validity_end: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Vendor-specific fields */}
                {VENDORS[selectedVendor]?.fields && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {VENDORS[selectedVendor].fields.map(field => (
                      <div key={field.name}>
                        <label className="text-sm font-medium text-slate-700 block mb-1">
                          {field.label}
                        </label>
                        {field.type === "select" ? (
                          <select
                            value={uploadForm[field.name] || field.default || ""}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                            className="w-full rounded-lg border-slate-200 text-sm"
                          >
                            <option value="">Select...</option>
                            {field.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            type={field.type}
                            value={uploadForm[field.name] || field.default || ""}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                            placeholder={field.hint}
                          />
                        )}
                        {field.hint && <p className="text-xs text-slate-500 mt-1">{field.hint}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setSelectedVendor(null)} data-testid="cancel-upload-btn">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={uploading || !uploadForm.file}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="upload-rate-card-btn"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Rate Card
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Rate Cards List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Metrans Rail Rate Cards */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Train className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Metrans Rail (FCL)</h3>
                <p className="text-sm text-slate-500">{rateCards.metrans.length} rate cards</p>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : rateCards.metrans.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Train className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No Metrans rate cards uploaded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {rateCards.metrans.map(card => (
                  <div key={card.id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <div>
                          <p className="font-medium text-slate-900">{card.name}</p>
                          <p className="text-xs text-slate-500">
                            {card.destination_country} • {card.entry_count} routes
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleExpand(card.id)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {expandedCards[card.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete("metrans", card.id)}
                          className="p-1 hover:bg-red-50 text-red-600 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {expandedCards[card.id] && (
                      <div className="mt-3 pt-3 border-t text-xs text-slate-500 space-y-1">
                        <div className="flex justify-between">
                          <span>Origin Ports:</span>
                          <span>{card.origin_ports?.join(", ")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Validity:</span>
                          <span>{card.validity_start} to {card.validity_end}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Uploaded:</span>
                          <span>{new Date(card.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* LCL Rate Cards */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">LCL Ocean Freight</h3>
                <p className="text-sm text-slate-500">{rateCards.lcl.length} rate cards</p>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : rateCards.lcl.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No LCL rate cards uploaded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {rateCards.lcl.map(card => (
                  <div key={card.id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {card.vendor_type === "primary" ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-blue-500" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{card.name}</p>
                          <p className="text-xs text-slate-500">
                            {card.vendor} • {card.direction} • {card.entry_count} routes
                            {card.vendor_type === "primary" && <span className="text-green-600 ml-1">(Primary)</span>}
                            {card.vendor_type === "reference" && <span className="text-blue-600 ml-1">(Reference)</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleExpand(card.id)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {expandedCards[card.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete("lcl", card.id)}
                          className="p-1 hover:bg-red-50 text-red-600 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {expandedCards[card.id] && (
                      <div className="mt-3 pt-3 border-t text-xs text-slate-500 space-y-1">
                        <div className="flex justify-between">
                          <span>Validity:</span>
                          <span>{card.validity_start} to {card.validity_end}</span>
                        </div>
                        {card.export_first_leg_addon && (
                          <div className="flex justify-between">
                            <span>Export 1st Leg Addon:</span>
                            <span>${card.export_first_leg_addon}/W/M</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span>Uploaded:</span>
                          <span>{new Date(card.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </>
        )}
      </main>
    </OffersModuleLayout>
  );
}
